import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { RowValuesWithEcomodClass } from "../api/models/rowValues.models";
import { EcomodClass } from "../api/models/ecomodClass.models";
import { useTranslation } from "react-i18next";
import { SupClass } from "../api/models/supClass.models";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { Locale, UIState } from "../types/ui-types";
import { getFormalLocale } from "../utils/appUtils";
import {
  getClassLabelFromAkeneoObject,
  getSecondaryClassLabelFromAkeneoObject,
} from "../utils/akeneoUtils";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Tooltip } from "primereact/tooltip";
import { Checkbox } from "primereact/checkbox";
import { OverlayPanel } from "primereact/overlaypanel";
import { Menu } from "primereact/menu";
import { useMaterials } from "../hooks/useMaterials";
import { InfoDot } from "./infoDot";
import { InputInstructionsHover } from "./inputInstructionsHover";

const Wrapper = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  font-size: 0.9rem;

  & .p-accordion-header-text {
    font-size: 0.9rem;
    font-weight: 500;
  }

  & .material-accordion.p-accordion-header:hover .p-button {
    color: var(--sumi-text-primary);
  }
`;

const Row = styled.div`
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;

  & > div {
    flex: 1;
    min-width: 200px;
  }
`;

const StyledDropdown = styled(Dropdown)`
  width: 100%;

  & .p-inputtext {
    font-size: 0.9rem;
  }
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  margin-bottom: 1rem;

  & .p-inputtext,
  & .p-inputnumber,
  & .p-dropdown {
    width: 100%;
    font-size: 0.9rem;
  }

  & label {
    font-size: 0.9rem;
    white-space: nowrap;
  }
`;

const NewMaterialContainer = styled.div<{ $alignLeft: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  position: relative;
`;

const AccordionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  white-space: nowrap;

  & .main-material {
    font-weight: 600;
    font-size: 0.9rem;
    display: flex;
    align-items: center;
  }

  & .ecomod-material {
    font-size: 0.7rem;
    text-overflow: ellipsis;
    overflow: hidden;
    max-width: 550px;
  }
`;

const Weight = styled.span`
  font-size: 0.9rem;
  font-weight: 500;
  margin-left: auto;
  color: var(--text-color-secondary);
  margin-right: 1rem;
`;

// extends the hover hit area into the .p-accordion-header-link padding so edges also trigger the tooltip
const AccordionHeaderHover = styled(InputInstructionsHover)`
  margin: -0.857rem -1rem;
  padding: 0.857rem 1rem;
`;

export enum ReusableClass {
  NONE = 0,
  FIRST_USE = 1,
  REUSABLE = 2,
}

enum UsageType {
  PACKAGED_IN_FINLAND = "Pakattu Suomessa",
  PACKAGED_ABROAD = "Pakattujen tuotteiden maahantuonti",
}

export enum CustomerType {
  BUSINESS = "Yritys",
  CONSUMER = "Kuluttaja",
}

export interface WizardSelection {
  id?: number;
  mainCategory?: string;
  customerType?: string;
  ecomodCategory?: string;
  supClass?: string;
  amount: number;
  reusableClass: ReusableClass;
  packagedAbroad?: boolean;
  secMaterial?: string; // secondary material type , same as ecomod type
  secValue?: number; // secondary material value
  secValueType?: 1 | 2; // 1 = kg, 2 = percentage
  isSup?: boolean;
  isEmptyCup?: boolean;
}

interface AddMaterialInputProps {
  setReportRowValues: (values: RowValuesWithEcomodClass[]) => void;
  ecomodClasses?: EcomodClass[];
  defaultValues?: WizardSelection[];
  supClasses?: SupClass[];
  preview?: boolean;
  alignLeft?: boolean;
  isMaterialCreation?: boolean;
  isSup?: boolean;
  supClass?: string;
}

const AddMaterialInputs: React.FC<AddMaterialInputProps> = ({
  setReportRowValues,
  ecomodClasses,
  defaultValues,
  supClasses,
  preview = false,
  alignLeft = false,
  isMaterialCreation = false,
  isSup = false,
  supClass,
}) => {
  const { t } = useTranslation();
  const overlayPanelRef = React.useRef<any>(null);
  const [locale] = useUIStore(useShallow((state: UIState) => [state.locale]));
  const formalLocale = getFormalLocale(locale);
  const plasticBagCodes = ["2D2", "2D3", "2A2", "2A3"];
  const businessOnlyCodes = ["1A3", "1B4"]; // Items like "Teollisuuden kuitupakkaukset"

  const [newWizardSelections, setNewWizardSelections] = useState<
    WizardSelection[]
  >(defaultValues || []);
  const [previousLength, setPreviousLength] = useState(0);

  useEffect(() => {
    if (!!defaultValues?.length && newWizardSelections.length === 0) {
      setNewWizardSelections(defaultValues);
    }
  }, [defaultValues]);

  const { multiMaterialPackageCodes } = useMaterials();

  const resolvedSupClass = isSup
    ? (supClasses?.find((category) => category.code === supClass) as
        | SupClass
        | undefined)
    : undefined;

  const sortedEcomodClasses = React.useMemo(() => {
    if (!ecomodClasses) return [];

    return [...ecomodClasses].sort((a, b) => {
      // Special handling for 2A2 and 2A3 codes
      if (a.code === "2A2" || a.code === "2A3") {
        return 1; // Move to the end
      }
      if (b.code === "2A2" || b.code === "2A3") {
        return -1; // Keep other items before these
      }

      // Default sorting by code
      return (a.code || "").localeCompare(b.code || "");
    });
  }, [ecomodClasses]);

  const materialOptions = [
    {
      label: t("label-consumer-material"),
      command: async () => {
        setNewWizardSelections((prev) => [
          ...prev,
          {
            mainCategory: undefined,
            ecomodCategory: undefined,
            customerType: CustomerType.CONSUMER,
            reusableClass: ReusableClass.NONE,
            amount: 0,
            origin: false,
          },
        ]);
        if (overlayPanelRef.current) overlayPanelRef.current.hide();
        // Open the newly added accordion tab
      },
    },
    {
      label: t("label-business-material"),
      command: async () => {
        setNewWizardSelections((prev) => [
          ...prev,
          {
            mainCategory: undefined,
            ecomodCategory: undefined,
            customerType: CustomerType.BUSINESS,
            reusableClass: ReusableClass.NONE,
            amount: 0,
            origin: false,
          },
        ]);
        if (overlayPanelRef.current) overlayPanelRef.current.hide();
        // Open the newly added accordion tab
      },
    },
  ];

  const getUsageTypeLabel = (type: UsageType | string) => {
    switch (type) {
      case UsageType.PACKAGED_IN_FINLAND:
        return t("label-packaged-in-finland");
      case UsageType.PACKAGED_ABROAD:
        return t("label-packaged-abroad");
      default:
        return "Tuntematon";
    }
  };

  // watch for changes in the new selections and update the reportRowValues
  React.useEffect(() => {
    const updatedReportRowValues: RowValuesWithEcomodClass[] =
      newWizardSelections.map((row, index) => {
        const newSelection = newWizardSelections[index];

        // if ecomodCategory is not selected, use mainCategory to find the ecomodClass
        const ecomodClass = newSelection.ecomodCategory
          ? (ecomodClasses?.find(
              (category) => category.code === newSelection.ecomodCategory,
            ) as EcomodClass)
          : (ecomodClasses?.find(
              (category) => category.code === newSelection.mainCategory,
            ) as EcomodClass);

        const secMaterial = newSelection.secMaterial
          ? (ecomodClasses?.find(
              (category) => category.code === newSelection.secMaterial,
            ) as EcomodClass)
          : undefined;

        return {
          id: newSelection.id,
          ecoModulationClass: ecomodClass,
          supClass: resolvedSupClass,
          businessUse:
            newSelection.customerType === undefined
              ? undefined
              : newSelection.customerType === CustomerType.BUSINESS,
          reusableClass: newSelection.reusableClass,
          packagedAbroad: newSelection.packagedAbroad ? true : false,
          value: newSelection.amount,
          secMaterial: secMaterial || null,
          secValue: newSelection.secValue || 0,
          secValueType: newSelection.secValueType || 1, // default to kg if
          isEmptyCup: newSelection.isEmptyCup,
        };
      });

    setReportRowValues(updatedReportRowValues);

    // Only scroll when a new item is added
    if (newWizardSelections.length > previousLength) {
      // Use setTimeout to ensure the DOM has updated
      setTimeout(() => {
        const accordionElements =
          document.querySelectorAll(`.material-accordion`);

        const accordionElement =
          accordionElements[accordionElements.length - 1];
        if (accordionElement) {
          accordionElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 100);
      setPreviousLength(newWizardSelections.length);
    } else if (newWizardSelections.length < previousLength) {
      // Update previous length when items are removed
      setPreviousLength(newWizardSelections.length);
    }
  }, [newWizardSelections, previousLength, resolvedSupClass]);

  const isInteger = (str: string) => {
    return /^[0-9]+$/.test(str);
  };

  const mainCategories = ecomodClasses
    ?.filter((category) => isInteger(category.code as string))
    .map((category) => ({
      label: getClassLabelFromAkeneoObject(locale, category),
      value: category.code,
    }));

  const secondaryCategories = ecomodClasses
    ?.filter((category) => category.secondary === true)
    .map((category) => ({
      label: getSecondaryClassLabelFromAkeneoObject(locale, category),
      value: category.code,
    }));

  const getReusableClassLabel = (type: ReusableClass | string) => {
    switch (type) {
      case ReusableClass.NONE:
        return t("label-not-reusable");
      case ReusableClass.FIRST_USE:
        return t("label-first-use");
      case ReusableClass.REUSABLE:
        return t("label-reusable");
      default:
        return "Tuntematon";
    }
  };

  const toMeaningfulNumber = (value: number) => {
    const roundedValue = Math.round(value * 100000) / 100000;
    return roundedValue.toLocaleString(formalLocale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 5,
    });
  };

  const clampSecondaryValue = (selection: WizardSelection) => {
    const secondaryAmount = selection.secValue;

    if (secondaryAmount === undefined || secondaryAmount === null) {
      return selection;
    }

    const maxValue =
      selection.secValueType === 2 ? 100 : (selection.amount ?? 0);

    return {
      ...selection,
      secValue: Math.min(secondaryAmount, maxValue),
    };
  };

  const isSupEnabled = newWizardSelections.some(
    (selection) =>
      selection.ecomodCategory &&
      (selection.ecomodCategory?.startsWith("2") ||
        selection.ecomodCategory?.startsWith("1")),
  );
  const multiMaterialAmountEligibleMainCategories = ["1", "2", "3"];

  const getAmountInstructionKey = (selection: WizardSelection) => {
    if (selection.ecomodCategory === "2A2") {
      return "input-instructions.consumer-2A2-singleUse-packagedInFinland";
    }

    if (
      selection.mainCategory === "5" &&
      (selection.reusableClass === ReusableClass.FIRST_USE ||
        selection.reusableClass === ReusableClass.REUSABLE)
    ) {
      return "input-instructions.business-5A2-reusable-reused";
    }

    const isMultiMaterial =
      !!selection.ecomodCategory &&
      multiMaterialPackageCodes.includes(selection.ecomodCategory);

    const isEligibleMainCategory =
      !!selection.mainCategory &&
      multiMaterialAmountEligibleMainCategories.includes(
        selection.mainCategory,
      );

    return isMultiMaterial && isEligibleMainCategory
      ? "input-instructions.add-material-amount"
      : undefined;
  };

  return (
    <Wrapper>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "2rem",
          width: "100%",
        }}
      >
        {!preview && (
          <NewMaterialContainer $alignLeft={alignLeft}>
            <div style={{ marginLeft: alignLeft ? "0" : "auto" }}>
              <InfoDot
                position="left"
                text={t("infotext-add-materials")}
                marginRight="10px"
              >
                <Button
                  label={t("cmd-add-material")}
                  icon="pi pi-plus"
                  type="button"
                  onClick={(e) => {
                    if (overlayPanelRef.current) {
                      overlayPanelRef.current.toggle(e);
                    }
                  }}
                  disabled={
                    isMaterialCreation && newWizardSelections.length >= 1
                  }
                />
              </InfoDot>
            </div>
            <OverlayPanel ref={overlayPanelRef}>
              <Menu model={materialOptions} closeOnEscape />
            </OverlayPanel>
          </NewMaterialContainer>
        )}
        <Accordion activeIndex={0}>
          {newWizardSelections.map((selection, index) => {
            const selectedKey = selection.mainCategory;
            const hasSecValueError =
              !!selection.secMaterial &&
              selection.secValueType !== 2 &&
              (selection.secValue ?? 0) > (selection.amount ?? 0);

            const ecomodOptions = selectedKey
              ? sortedEcomodClasses
                  ?.filter(
                    (category) =>
                      category.code?.startsWith(selectedKey) &&
                      selectedKey !== category.code &&
                      // if selectedKey/mainCategory is 5 (Puupakkaukset), filter out the 5A1 class from consumers
                      !(
                        selectedKey === "5" &&
                        category.code === "5A1" &&
                        selection.customerType === CustomerType.CONSUMER
                      ) &&
                      // filter out plastic bag codes for business customers
                      !(
                        selection.customerType === CustomerType.BUSINESS &&
                        plasticBagCodes.includes(category.code as string)
                      ) &&
                      // filter out business-only codes for consumer customers
                      !(
                        selection.customerType === CustomerType.CONSUMER &&
                        businessOnlyCodes.includes(category.code as string)
                      ),
                  )
                  .map((category) => ({
                    label: getClassLabelFromAkeneoObject(locale, category),
                    value: category.code,
                  }))
              : [];

            const subEcoClass = ecomodClasses?.find(
              (category) =>
                category.code === (selection.ecomodCategory as string),
            );

            const ecomodName = subEcoClass
              ? getClassLabelFromAkeneoObject(
                  locale,
                  subEcoClass as EcomodClass,
                )
              : "";

            const mainEcoClass = ecomodClasses?.find(
              (category) =>
                category.code === (selection.mainCategory as string),
            );

            const selectionName = mainEcoClass
              ? getClassLabelFromAkeneoObject(
                  locale,
                  mainEcoClass as EcomodClass,
                )
              : "";

            const isOtherMaterialsClass = mainEcoClass?.code === "6";

            const accordionHeaderRow = (
              <div
                className={`material-accordion-header-${index}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <AccordionHeader>
                  <div className="main-material">
                    {selectionName || t("label-new-material")}{" "}
                    {`${selection.customerType === CustomerType.CONSUMER ? `(${t("label-consumer")})` : `(${t("label-business")})`}`}
                  </div>

                  <Tooltip target=".ecomod-material" />
                  <span
                    className="ecomod-material"
                    data-pr-tooltip={ecomodName}
                    data-pr-position="bottom"
                  >
                    {ecomodName}
                  </span>
                </AccordionHeader>
                <Weight>
                  {selection.amount
                    ? `${toMeaningfulNumber(selection.amount)} kg`
                    : ""}
                </Weight>

                <span>
                  <Button
                    style={{
                      textDecoration: "underline",
                      color: "var(--sumi-text-primary)",
                    }}
                    text
                    type="button"
                    label={t("cmd-remove")}
                    onClick={(e) => {
                      e.stopPropagation();
                      const updatedSelections = [...newWizardSelections];
                      updatedSelections.splice(index, 1);
                      setNewWizardSelections(updatedSelections);
                    }}
                  />
                </span>
              </div>
            );

            return (
              <AccordionTab
                key={index}
                className={"material-accordion"}
                header={
                  isOtherMaterialsClass ? (
                    <AccordionHeaderHover translationKey="input-instructions.material-tab-6">
                      {accordionHeaderRow}
                    </AccordionHeaderHover>
                  ) : (
                    accordionHeaderRow
                  )
                }
              >
                <InputContainer key={index}>
                  <label>{t("header-material")}</label>
                  <StyledDropdown
                    value={selection.mainCategory}
                    options={mainCategories}
                    onChange={(e) => {
                      const updatedSelections = [...newWizardSelections];
                      updatedSelections[index].mainCategory = e.value;
                      updatedSelections[index].ecomodCategory = undefined;
                      setNewWizardSelections(updatedSelections);
                    }}
                    placeholder={t("label-select-material")}
                    disabled={preview}
                  />
                </InputContainer>

                <InputContainer>
                  <label>{t("header-ecomodulation")}</label>
                  <StyledDropdown
                    disabled={
                      !selection.mainCategory ||
                      !selection.customerType ||
                      preview
                    }
                    value={selection.ecomodCategory}
                    options={ecomodOptions}
                    onChange={(e) => {
                      const updatedSelections = [...newWizardSelections];
                      updatedSelections[index].ecomodCategory = e.value;
                      setNewWizardSelections(updatedSelections);
                      // reset secondary material if ecomodCategory changes
                      updatedSelections[index].secMaterial = undefined;
                      updatedSelections[index].secValue = undefined;
                      updatedSelections[index].secValueType = undefined;
                    }}
                    placeholder={t("label-select-ecomodulation")}
                    showClear
                  />
                </InputContainer>

                {(selection.mainCategory === "1" ||
                  selection.mainCategory === "2") && (
                  <Row>
                    <InputContainer style={{ flex: "0 0 200px" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          marginTop: "10px",
                          marginBottom: "5px",
                        }}
                      >
                        <Checkbox
                          inputId={`isSoldEmptyCup-${index}`}
                          checked={selection.isEmptyCup || false}
                          onChange={(e) => {
                            const updatedSelections = [...newWizardSelections];
                            updatedSelections[index].isEmptyCup = e.checked;
                            setNewWizardSelections(updatedSelections);
                          }}
                          disabled={preview}
                        />
                        <label htmlFor={`isSoldEmptyCup-${index}`}>
                          {t("label-sold-empty-cup")}
                        </label>
                      </div>
                    </InputContainer>
                  </Row>
                )}

                <Row>
                  <InputContainer>
                    <InfoDot
                      position="right"
                      text={t("infotext-reusability")}
                      marginLeft="10px"
                    >
                      <label>{t("header-reusability")}</label>
                    </InfoDot>
                    <StyledDropdown
                      disabled={
                        !selection.mainCategory ||
                        !selection.customerType ||
                        preview
                      }
                      value={selection.reusableClass}
                      options={[
                        {
                          label: getReusableClassLabel(ReusableClass.NONE),
                          value: ReusableClass.NONE,
                        },
                        {
                          label: getReusableClassLabel(ReusableClass.FIRST_USE),
                          value: ReusableClass.FIRST_USE,
                        },
                        {
                          label: getReusableClassLabel(ReusableClass.REUSABLE),
                          value: ReusableClass.REUSABLE,
                        },
                      ]}
                      onChange={(e) => {
                        const updatedSelections = [...newWizardSelections];
                        updatedSelections[index].reusableClass = e.value;
                        setNewWizardSelections(updatedSelections);
                      }}
                      placeholder={t("label-select-reusability")}
                    />
                  </InputContainer>

                  <InputContainer>
                    <label>{t("header-package-origin")}</label>
                    <StyledDropdown
                      disabled={
                        !selection.mainCategory ||
                        !selection.customerType ||
                        preview
                      }
                      value={!!selection.packagedAbroad}
                      options={[
                        {
                          label: getUsageTypeLabel(
                            UsageType.PACKAGED_IN_FINLAND,
                          ),
                          value: false,
                        },
                        {
                          label: getUsageTypeLabel(UsageType.PACKAGED_ABROAD),
                          value: true,
                        },
                      ]}
                      onChange={(e) => {
                        const updatedSelections = [...newWizardSelections];
                        updatedSelections[index].packagedAbroad = e.value;
                        setNewWizardSelections(updatedSelections);
                      }}
                      placeholder={t("label-select-origin")}
                    />
                  </InputContainer>

                  <InputContainer>
                    <label>
                      {isMaterialCreation
                        ? t("header-total-amount-kg")
                        : t("header-amount-kg")}
                    </label>
                    <InputInstructionsHover
                      translationKey={getAmountInstructionKey(selection)}
                    >
                      <InputNumber
                        disabled={
                          !selection.mainCategory ||
                          !selection.customerType ||
                          preview
                        }
                        value={selection.amount}
                        onChange={(e) => {
                          const updatedSelections = [...newWizardSelections];
                          const newAmount = e.value !== null ? e.value : 0;
                          updatedSelections[index].amount = newAmount;
                          setNewWizardSelections(updatedSelections);
                        }}
                        mode="decimal"
                        locale={formalLocale}
                        minFractionDigits={0}
                        maxFractionDigits={6}
                        min={0}
                        max={1000000}
                      />
                    </InputInstructionsHover>
                  </InputContainer>
                </Row>

                {selection.ecomodCategory &&
                  multiMaterialPackageCodes.includes(
                    selection.ecomodCategory as string,
                  ) && (
                    <Row>
                      <InputContainer>
                        <label>{t("header-secondary-material")}</label>
                        <StyledDropdown
                          value={selection.secMaterial}
                          options={secondaryCategories}
                          onChange={(e) => {
                            const updatedSelections = [...newWizardSelections];
                            updatedSelections[index].secMaterial = e.value;
                            setNewWizardSelections(updatedSelections);
                          }}
                          placeholder={t("label-select-secondary-material")}
                          showClear
                          disabled={
                            !selection.ecomodCategory ||
                            !multiMaterialPackageCodes.includes(
                              selection.ecomodCategory as string,
                            ) ||
                            preview
                          }
                        />
                        {!selection.secMaterial && (
                          <small
                            style={{
                              color: "var(--sumi-error-color)",
                              fontSize: "0.75rem",
                              marginTop: "-0.35rem",
                              marginLeft: "0.25rem",
                            }}
                          >
                            {t("label-required") || "Required"}
                          </small>
                        )}
                      </InputContainer>
                      <InputContainer>
                        <label>
                          {t("header-secondary-material-value-type")}
                        </label>
                        <StyledDropdown
                          value={selection.secValueType}
                          options={[
                            {
                              label: t("label-kg"),
                              value: 1,
                            },
                            {
                              label: t("label-percentage"),
                              value: 2,
                            },
                          ]}
                          onChange={(e) => {
                            const updatedSelections = [...newWizardSelections];
                            updatedSelections[index].secValueType = e.value;
                            setNewWizardSelections(updatedSelections);
                          }}
                          disabled={!selection.secMaterial || preview}
                        />
                        {!selection.secValueType && (
                          <small
                            style={{
                              color: "var(--sumi-error-color)",
                              fontSize: "0.75rem",
                              marginTop: "-0.35rem",
                              marginLeft: "0.25rem",
                            }}
                          >
                            {t("label-required") || "Required"}
                          </small>
                        )}
                      </InputContainer>
                      <InputContainer>
                        <label>{t("header-secondary-material-value")}</label>
                        <InputNumber
                          value={selection.secValue}
                          onChange={(e) => {
                            const updatedSelections = [...newWizardSelections];
                            const secondaryValue =
                              e.value !== null ? e.value : 0;
                            const maxValue =
                              updatedSelections[index].secValueType === 2
                                ? 100
                                : undefined;

                            updatedSelections[index].secValue =
                              maxValue !== undefined
                                ? Math.min(secondaryValue, maxValue)
                                : secondaryValue;
                            setNewWizardSelections(updatedSelections);
                          }}
                          disabled={!selection.secMaterial || preview}
                          mode="decimal"
                          locale={formalLocale}
                          minFractionDigits={0}
                          maxFractionDigits={6}
                          min={0}
                          max={selection.secValueType === 2 ? 100 : undefined}
                          className={hasSecValueError ? "p-invalid" : ""}
                          tooltip={
                            hasSecValueError
                              ? t("tooltip-secondary-value-exceeds-total")
                              : undefined
                          }
                          tooltipOptions={{ position: "top" }}
                        />
                        {!selection.secValue && !hasSecValueError && (
                          <small
                            style={{
                              color: "var(--sumi-error-color)",
                              fontSize: "0.75rem",
                              marginTop: "-0.35rem",
                              marginLeft: "0.25rem",
                            }}
                          >
                            {t("label-required") || "Required"}
                          </small>
                        )}
                        {hasSecValueError && (
                          <small
                            style={{
                              color: "var(--sumi-error-color)",
                              fontSize: "0.75rem",
                              marginTop: "-0.35rem",
                              marginLeft: "0.25rem",
                            }}
                          >
                            {t("error-secondary-value-exceeds-total") ||
                              "Toissijaisen materiaalin arvo ylitt\u00e4\u00e4 pakkauksen kokonaispainon"}
                          </small>
                        )}
                      </InputContainer>
                    </Row>
                  )}

                <Row style={{ justifyContent: "flex-end", marginTop: "1rem" }}>
                  <Button
                    id="material-accordion-ok-button"
                    outlined
                    type="button"
                    label={t("cmd-ok")}
                    disabled={hasSecValueError}
                    onClick={(e) => {
                      e.stopPropagation();
                      // Close the tab by collapsing the Accordion (no removal)
                      const accordion = document.querySelectorAll(
                        `.material-accordion-header-${index}`,
                      )[0];
                      if (accordion) {
                        (accordion as HTMLElement).click();
                      }
                    }}
                  />
                </Row>
              </AccordionTab>
            );
          })}
        </Accordion>
      </div>
    </Wrapper>
  );
};

export default AddMaterialInputs;
